import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { logActivity } from "@/lib/cms";
import { useToast } from "@/context/ToastContext";
import type { SocialLink } from "@/lib/types";
import {
  PageHeader,
  Button,
  Card,
  Spinner,
  EmptyState,
  Toggle,
  Field,
  Input,
  Modal,
} from "@/pages/admin/components/ui";

interface Draft {
  nombre: string;
  url: string;
  icono: string;
  orden: number;
  activo: boolean;
}

export default function Redes() {
  const { success, error: toastError } = useToast();
  const [items, setItems] = useState<SocialLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<SocialLink | null>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>({ nombre: "", url: "", icono: "ri-link", orden: 0, activo: true });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await supabase.from("social_links").select("*").order("orden");
    setItems((data ?? []) as SocialLink[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function openEdit(s: SocialLink) {
    setEditing(s);
    setDraft({ nombre: s.nombre, url: s.url || "", icono: s.icono || "ri-link", orden: s.orden, activo: s.activo });
    setOpen(true);
  }

  function openNew() {
    setEditing(null);
    setDraft({ nombre: "", url: "", icono: "ri-link", orden: items.length + 1, activo: true });
    setOpen(true);
  }

  async function save() {
    if (!draft.nombre.trim()) {
      toastError("El nombre es obligatorio.");
      return;
    }
    if (draft.url && !/^https?:\/\//i.test(draft.url)) {
      toastError("La URL debe comenzar con https://");
      return;
    }
    setSaving(true);
    const payload = { nombre: draft.nombre, url: draft.url || null, icono: draft.icono || null, orden: draft.orden, activo: draft.activo };
    const { error } = editing
      ? await supabase.from("social_links").update(payload).eq("id", editing.id)
      : await supabase.from("social_links").insert(payload);
    setSaving(false);
    if (error) {
      toastError("No se pudieron guardar los cambios.");
      return;
    }
    await logActivity(editing ? "modificó una red social" : "creó una red social", draft.nombre);
    success("Red social guardada correctamente.");
    setOpen(false);
    load();
  }

  async function toggle(s: SocialLink) {
    await supabase.from("social_links").update({ activo: !s.activo }).eq("id", s.id);
    load();
  }

  async function move(s: SocialLink, dir: -1 | 1) {
    const sorted = [...items].sort((a, b) => a.orden - b.orden);
    const idx = sorted.findIndex((x) => x.id === s.id);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= sorted.length) return;
    await supabase.from("social_links").update({ orden: sorted[target].orden }).eq("id", s.id);
    await supabase.from("social_links").update({ orden: sorted[idx].orden }).eq("id", sorted[target].id);
    load();
  }

  const sorted = [...items].sort((a, b) => a.orden - b.orden);

  return (
    <div>
      <PageHeader
        title="Redes sociales"
        subtitle="Las redes desactivadas no aparecen en el sitio público."
        actions={<Button onClick={openNew}><i className="ri-add-line" />Nueva red</Button>}
      />

      {loading ? (
        <Spinner label="Cargando redes…" />
      ) : items.length === 0 ? (
        <Card><EmptyState icon="ri-share-line" title="Aún no hay redes sociales" action={<Button onClick={openNew}><i className="ri-add-line" />Nueva red</Button>} /></Card>
      ) : (
        <div className="space-y-3">
          {sorted.map((s, i) => (
            <Card key={s.id} className="p-4 flex items-center gap-4">
              <div className="w-11 h-11 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
                <i className={`${s.icono || "ri-link"} text-xl`} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-foreground-900">{s.nombre}</p>
                <p className="text-xs text-foreground-500 truncate">{s.url || "Sin enlace configurado"}</p>
              </div>
              <Toggle checked={s.activo} onChange={() => toggle(s)} />
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => move(s, -1)} disabled={i === 0} className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 disabled:opacity-30"><i className="ri-arrow-up-line" /></button>
                <button onClick={() => move(s, 1)} disabled={i === sorted.length - 1} className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 disabled:opacity-30"><i className="ri-arrow-down-line" /></button>
                <button onClick={() => openEdit(s)} className="w-8 h-8 flex items-center justify-center rounded-md text-primary-600 hover:bg-primary-50"><i className="ri-edit-line" /></button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? "Editar red social" : "Nueva red social"}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving}>{saving ? "Guardando…" : "Guardar"}</Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Nombre" required>
            <Input value={draft.nombre} onChange={(e) => setDraft((d) => ({ ...d, nombre: e.target.value }))} />
          </Field>
          <Field label="URL" hint="Debe comenzar con https://">
            <Input value={draft.url} onChange={(e) => setDraft((d) => ({ ...d, url: e.target.value }))} placeholder="https://…" />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Icono (Remix)" hint="Ej. ri-instagram-line">
              <Input value={draft.icono} onChange={(e) => setDraft((d) => ({ ...d, icono: e.target.value }))} />
            </Field>
            <Field label="Orden">
              <Input type="number" value={draft.orden} onChange={(e) => setDraft((d) => ({ ...d, orden: Number(e.target.value) }))} />
            </Field>
          </div>
          <Toggle checked={draft.activo} onChange={(v) => setDraft((d) => ({ ...d, activo: v }))} label="Activo" />
        </div>
      </Modal>
    </div>
  );
}